/**
 * Scripted storefront assistant.
 *
 * Answers are matched against the keyword table supplied by
 * snippets/ai-assistant.liquid. Replacing `respond()` with a fetch to the
 * model endpoint is the only change needed once a backend exists.
 */

const THINKING_MS = 520;
const PROMPT_COUNT = 3;

class AiAssistant extends HTMLElement {
  #data = { greeting: '', fallback: '', entries: [] };
  #answered = new Set();
  #pending = null;

  connectedCallback() {
    const source = this.querySelector('script[data-ai-assistant-data]');
    if (source) this.#data = JSON.parse(source.textContent);

    this.panel = this.querySelector('[data-ai-assistant-panel]');
    this.log = this.querySelector('[data-ai-assistant-log]');
    this.prompts = this.querySelector('[data-ai-assistant-prompts]');
    this.form = this.querySelector('[data-ai-assistant-form]');
    this.input = this.querySelector('[data-ai-assistant-input]');

    this.querySelector('[data-ai-assistant-open]')?.addEventListener('click', () => this.open());
    this.querySelector('[data-ai-assistant-close]')?.addEventListener('click', () => this.close());
    this.form?.addEventListener('submit', (event) => {
      event.preventDefault();
      const value = this.input.value.trim();
      if (!value) return;
      this.input.value = '';
      this.#ask(value);
    });

    this.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') this.close();
    });

    this.#renderPrompts();
  }

  disconnectedCallback() {
    clearTimeout(this.#pending);
  }

  open() {
    this.panel.hidden = false;
    this.dataset.open = 'true';
    this.input?.focus();
  }

  close() {
    this.panel.hidden = true;
    this.dataset.open = 'false';
    this.querySelector('[data-ai-assistant-open]')?.focus();
  }

  #ask(question) {
    this.#append(question, 'user');
    this.#think(() => this.#append(this.#respond(question), 'agent'));
  }

  /** Replace this with a request to the assistant endpoint. */
  #respond(question) {
    const words = question.toLowerCase().match(/[\p{L}\p{N}']+/gu) ?? [];
    let best = null;
    let bestScore = 0;

    for (const entry of this.#data.entries) {
      const score = entry.keywords.reduce(
        (total, keyword) => total + (words.some((word) => word.startsWith(keyword)) ? 1 : 0),
        0
      );
      if (score > bestScore) {
        best = entry;
        bestScore = score;
      }
    }

    if (!best) return this.#data.fallback;

    this.#answered.add(best.label);
    this.#renderPrompts();
    return best.answer;
  }

  /** Holds the turn open with a typing indicator so replies do not snap in. */
  #think(done) {
    const indicator = document.createElement('p');
    indicator.className = 'ai-assistant__typing';
    indicator.innerHTML = '<span></span><span></span><span></span>';
    this.log.append(indicator);
    this.#scroll();

    this.#pending = setTimeout(() => {
      indicator.remove();
      done();
    }, THINKING_MS);
  }

  #append(text, role) {
    const message = document.createElement('p');
    message.className = `ai-assistant__msg ai-assistant__msg--${role}`;
    message.textContent = text;
    this.log.append(message);
    this.#scroll();
  }

  #scroll() {
    this.log.scrollTo({ top: this.log.scrollHeight, behavior: 'smooth' });
  }

  #renderPrompts() {
    const remaining = this.#data.entries
      .filter((entry) => !this.#answered.has(entry.label))
      .slice(0, PROMPT_COUNT);

    this.prompts.replaceChildren(
      ...remaining.map((entry) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'ai-assistant__prompt';
        button.textContent = entry.label;
        button.addEventListener('click', () => this.#ask(entry.question));
        return button;
      })
    );
  }
}

if (!customElements.get('ai-assistant')) {
  customElements.define('ai-assistant', AiAssistant);
}
